import { Card, Notice } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import type { NextStep } from '@core/bookings/nextStep';

/**
 * What happens next on a booking, said under where it stands — the booking redesign's step 4 on
 * the website, from the rule the phone's pinned button reads (`@core/bookings/nextStep`). Since
 * 2026-10-22 it heads the booking page's right-hand column, the button full width under the words.
 *
 * <p>
 * The button is the caller's, passed as children: it owns the handlers, and settling the deposit
 * asks how the refund goes back before it is sent. Everything else stays in the quieter row at the
 * foot of the page, which no longer repeats this one.
 * </p>
 */
export function NextStepCard({ step, children }: { step: NextStep; children?: React.ReactNode }) {
  // Nothing to press while the garments are being got ready, so it is a sentence, not a button.
  if (step.kind === 'waiting') {
    return (
      <Notice tone="info" title={step.label}>
        {step.detail}
      </Notice>
    );
  }

  const late = step.kind === 'takeBack' && step.late;

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="min-w-0">
          <p className="text-default mb-0.5 text-[length:var(--scale-list-meta)] font-semibold tracking-wider uppercase">
            What happens next
          </p>
          <p className="mb-0 flex flex-wrap items-center gap-2 text-lg font-bold text-gray-900">
            {step.label}
            {/* The word as well as the colour, as everywhere a late item shows. */}
            {late && <StatusBadge tone="negative">Late</StatusBadge>}
          </p>
          {step.detail && (
            <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">{step.detail}</p>
          )}
        </div>
        {/* The page's button, full width under the words — the phone's pinned button. */}
        {children && (
          <div className="flex flex-col [&_button]:h-11 [&_button]:w-full [&_button]:justify-center">
            {children}
          </div>
        )}
      </div>
    </Card>
  );
}
